import { Button, CircularProgress, Grid, makeStyles, Typography } from '@material-ui/core';
import PlayArrowRounded from '@material-ui/icons/PlayArrowRounded';
import { PropsWithChildren, useState } from 'react';
import TrashSolid from '@material-ui/icons/Delete';
import ClockLock from '@eduzz/houston-icons/TimerOutline';
import defaultImage from 'assets/img/default-lesson.png';
import LockOutlined from '@material-ui/icons/LockOutlined';
import { useHistory } from 'react-router';
import { buildLessonUrl } from 'utils/buildUrls';
import { saveLesson } from 'services/savedLessons';
import { enStatus, blockedStatus } from 'interface/course';
import Alert from 'components/Shared/Alert';
import { dateFormater } from 'utils';
import { ISavedLesson } from 'interface/savedLesson';

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: 30,
    marginBottom: 30,
    [theme.breakpoints.up('lg')]: {
      flexWrap: 'nowrap',
    },
    [theme.breakpoints.down('md')]: {
      flexDirection: 'row',
      background: theme.palette.grey[800],
      paddingBottom: 20,
      maxWidth: 400,
      width: '100%',
      boxSizing: 'border-box',
      alignItems: 'flex-start',
      margin: 0,
    },
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
    },
  },
  gutter: {
    [theme.breakpoints.down('md')]: {
      padding: 20,
    },
  },
  image: {
    width: 150,
    height: 110,
    borderRadius: 8,
    objectFit: 'cover',
    [theme.breakpoints.down('md')]: {
      marginBottom: 20,
      borderRadius: 0,
      width: '100%',
      height: 'auto',
      alignSelf: 'flex-start',
    },
  },
  title: {
    fontWeight: 600,
    fontSize: 20,
    marginBottom: 10,
    color: theme.palette.common.white,
    maxWidth: 700,
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 2,
    overflow: 'hidden',
    wordBreak: 'break-word',
    [theme.breakpoints.down('md')]: {
      marginBottom: 0,
      fontSize: '100%',
    },
  },
  description: {
    fontSize: 20,
    display: '-webkit-box',
    '-webkit-box-orient': 'vertical',
    '-webkit-line-clamp': 2,
    overflow: 'hidden',
    wordBreak: 'break-word',
    color: theme.palette.common.white,
    [theme.breakpoints.down('md')]: {
      fontSize: '100%',
    },
  },
  titleContainer: {
    margin: 20,
    height: '100%',
    width: '65%',
    [theme.breakpoints.down('md')]: {
      width: '100%',
    },
  },
  actions: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      alignSelf: 'flex-start',
      marginTop: 'auto',
      marginRight: 20,
      marginLeft: 20,
    },
    [theme.breakpoints.down('xs')]: {
      marginTop: 'auto',
    },
  },
  notBlockedButton: {
    border: `1px solid ${theme.palette.common.white}`,
    textTransform: 'none',
    fontWeight: 600,
    '& span': {
      fontSize: 14,
      color: theme.palette.common.white,
      marginLeft: 0,
    },
    padding: '8px 20px',
    marginRight: 16,
    [theme.breakpoints.down('xs')]: {
      padding: '8px 10px',
    },
  },
  buttonIcon: {
    '& path': {
      color: theme.palette.primary.main,
    },
  },
  deleteIconButton: {
    width: 24,
    height: 24,
    '& svg': {
      transition: '0.5s',
      fontSize: 24,
      cursor: 'pointer',
      color: theme.palette.common.white,
      '&:hover': {
        color: theme.palette.grey[300],
      },
    },
  },
  blockedLesson: {
    display: 'flex',
    marginRight: 20,
    '& svg': {
      width: 20,
      height: 20,
    },
    '& p, svg': {
      color: theme.palette.common.white,
    },
    '& > p': {
      marginRight: 10,
      fontWeight: 600,
    },
  },
}));

interface IProps extends ISavedLesson {
  handleRemove: (lessonId: number) => void;
}

const SavedLessonCard = (props: PropsWithChildren<IProps>) => {
  const [loading, setLoading] = useState(false);
  const classes = useStyles();

  const imageWithFallback = props.image || defaultImage;

  const history = useHistory();

  const handleGoToLesson = () => {
    history.push(buildLessonUrl(props.course_hash, props.id));
  };

  const handleUnsaveLesson = async () => {
    const confirm = await Alert.confirm({ message: 'Deseja remover esta aula da sua lista de aulas salvas?' });
    if (!confirm) return;
    props.handleRemove(props.id);
    setLoading(true);
    saveLesson(props.id).finally(() => {
      setLoading(false);
    });
  };

  const renderLessonStatus = () => {
    const mapper = {
      [enStatus.BLOCKED]: { title: 'Aula Bloqueada', icon: <LockOutlined /> },
      [enStatus.NOT_RELEASED]: {
        title: props.release ? `Aula agendada - ${dateFormater(props.release)}` : 'Aula agendada',
        icon: <ClockLock />,
      },
      [enStatus.EXPIRED]: { title: 'Aula expirada', icon: <ClockLock /> },
      [enStatus.MUST_ACCEPT_TERM]: { title: 'É necessário aceitar os termos', icon: <LockOutlined /> },
    };

    if (blockedStatus.includes(props.status)) {
      const { title, icon } = mapper[props.status];
      return (
        <div className={classes.blockedLesson}>
          <Typography variant='body2'>{title}</Typography>
          {icon}
        </div>
      );
    }

    return (
      <Button
        className={classes.notBlockedButton}
        onClick={handleGoToLesson}
        endIcon={<PlayArrowRounded className={classes.buttonIcon} />}
        id='savedLesson-go-to-lesson'
      >
        Acessar
      </Button>
    );
  };

  return (
    <Grid className={classes.gutter}>
      <Grid className={classes.container} alignItems={'center'} container>
        <img className={classes.image} src={imageWithFallback} alt={'imagem da aula'} />
        <Grid className={classes.titleContainer}>
          <Typography className={classes.title} id='savedLesson-lesson-title'>
            {props.title}
          </Typography>
          <Typography className={classes.description} id='savedLesson-lesson-description'>
            {props.course_title}
          </Typography>
        </Grid>
        <Grid className={classes.actions}>
          {renderLessonStatus()}
          <div className={classes.deleteIconButton} onClick={handleUnsaveLesson} id='savedLesson-delete-lesson'>
            {!loading && <TrashSolid />}
            {loading && <CircularProgress size={24} />}
          </div>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default SavedLessonCard;
